// /badge/s/:shareId, public (works logged out). The member's badge as a 9:16
// story: the page shows it live, "Save image" downloads the same picture as a
// 1080 x 1920 PNG, "Share" hands the PNG (or the link) to the share sheet.
import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useGetSharedBadgeQuery } from "../../../services/rtkApi/badgesApi";
import { useAppSelector } from "../../../store/hooks";
import { useBadgeLib } from "../art";
import type { SharedBadgeResponse } from "../types";
import BadgeStoryFrame from "./BadgeStoryFrame";
import { downloadBlob, shareStory } from "./shareActions";
import { drawBadgeStoryPng, storyFileName } from "./storyImage";
import type { StoryInput } from "./storyTheme";
import "./badgeShare.css";

/**
 * The story inputs from the share response: how many hold the badge now, else how
 * many held it when it was earned. The tier is the rarity.
 */
export function storyInputFromShare(data: SharedBadgeResponse): StoryInput {
  const current = data.badge.rarity;
  const atEarn = data.rarityAtEarn;
  return {
    badge: {
      code: data.badge.code,
      name: data.badge.name,
      category: data.badge.category,
      tier: data.badge.tier,
      number: data.badge.number,
      unique: data.badge.unique,
    },
    username: data.username,
    count: Math.max(1, data.count || 1),
    date: data.firstEarnedAt,
    holders: current?.holders ?? atEarn?.holders ?? 0,
    members: current?.members ?? atEarn?.members ?? 0,
    firstEver: !!data.firstEver,
  };
}

function isNotFound(error: unknown): boolean {
  const status = (error as FetchBaseQueryError | undefined)?.status;
  return status === 404 || status === 400;
}

type StoryState = "idle" | "working" | "ready" | "failed";

const BadgeSharePage: React.FC = () => {
  const { shareId = "" } = useParams<{ shareId: string }>();
  const auth = useAppSelector((s) => s.auth);
  const loggedIn = auth.bootstrapStatus === "ready" && !!auth.token;
  const { data, error, isLoading, refetch } = useGetSharedBadgeQuery(shareId, { skip: !shareId });
  const { lib, error: libError } = useBadgeLib();
  const input = useMemo(() => (data ? storyInputFromShare(data) : null), [data]);

  const [story, setStory] = useState<Blob | null>(null);
  const [storyState, setStoryState] = useState<StoryState>("idle");

  useEffect(() => {
    if (data) document.title = `${data.badge.name}, earned by ${data.username} | n450s.com`;
    else document.title = "Badge | n450s.com";
  }, [data]);

  // Render the PNG ahead of time, once the art library has settled either way.
  useEffect(() => {
    if (!input || (!lib && !libError)) return undefined;
    let live = true;
    setStoryState("working");
    drawBadgeStoryPng(input, { lib: lib ?? null })
      .then((blob) => {
        if (!live) return;
        setStory(blob);
        setStoryState("ready");
      })
      .catch(() => {
        if (live) setStoryState("failed");
      });
    return () => {
      live = false;
    };
  }, [input, lib, libError]);

  const shareUrl = `${typeof window !== "undefined" ? window.location.origin : "https://n450s.com"}/badge/s/${encodeURIComponent(shareId)}`;
  const filename = storyFileName(data?.badge.code || "badge");

  const onSave = () => {
    if (story) downloadBlob(story, filename);
    else if (storyState === "failed") toast.error("This browser could not make the image. A screenshot works too.");
  };

  const onShare = async () => {
    if (!data) return;
    const outcome = await shareStory({
      blob: story,
      filename,
      url: shareUrl,
      title: `${data.badge.name} on n450s.com`,
      text: `${data.username} earned ${data.badge.name} on n450s.com`,
    });
    if (outcome === "copied") toast.success("Link copied");
    else if (outcome === "failed") toast.error("Could not share from here. Copy the address instead.");
  };

  let body: React.ReactNode;
  if (!shareId || (error && isNotFound(error))) {
    body = (
      <div className="bshare__note" role="alert">
        <h1>This badge link does not exist.</h1>
        <p>It may have been mistyped. The rest of the site is still here.</p>
        <Link className="bshare__btn" to="/">
          Go to n450s.com
        </Link>
      </div>
    );
  } else if (error) {
    body = (
      <div className="bshare__note" role="alert">
        <h1>Could not load this badge right now.</h1>
        <p>Try again in a minute.</p>
        <button type="button" className="bshare__btn" onClick={() => refetch()}>
          Try again
        </button>
      </div>
    );
  } else if (isLoading || !input || !data) {
    body = (
      <div className="bshare__frame bshare__frame--loading" aria-busy="true">
        <span>Loading the badge</span>
      </div>
    );
  } else {
    body = (
      <>
        <div className="bshare__frame">
          <BadgeStoryFrame input={input} />
        </div>
        <div className="bshare__actions">
          <button type="button" className="bshare__btn bshare__btn--primary" onClick={onSave} disabled={storyState !== "ready"}>
            {storyState === "working" || storyState === "idle" ? "Preparing image" : "Save image"}
          </button>
          <button type="button" className="bshare__btn" onClick={onShare}>
            Share
          </button>
        </div>
        <p className="bshare__hint">
          {storyState === "failed"
            ? "This browser could not make the image. A screenshot of the card works too."
            : "The saved image is 1080 x 1920, ready for a story."}
        </p>
        {loggedIn ? (
          <Link className="bshare__link" to={`/badges/u/${encodeURIComponent(data.username)}`}>
            See all of {data.username}&apos;s badges
          </Link>
        ) : (
          <Link className="bshare__link" to="/">
            What is n450s.com?
          </Link>
        )}
      </>
    );
  }

  return (
    <div className="bshare" data-no-status-spacer>
      <main className="bshare__main">{body}</main>
    </div>
  );
};

export default BadgeSharePage;
